import { useMemo, type ReactNode } from 'react'
import { Lock, Truck } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  EditableGrid,
  type EditableColumnDef,
} from '@/components/ui/EditableGrid/EditableGrid'
import { IngredientPicker } from './IngredientPicker'
import type { Ingredient } from '../api/types'

/**
 * EditableGrid wrapper specialised for formulation ingredient rows.
 *
 * Column map:
 *  - Phase           (text)
 *  - Trade name      (ProductPicker cell — picker populates product_id + trade_name)
 *  - INCI            (comma-separated text → array)
 *  - Function        (text)
 *  - Percent %       (numeric, stored as decimal 0.05 = 5%; displayed as %)
 *  - Target weight g (read-only, computed: percent × batch_size_g)
 *  - Notes           (text)
 *
 * %-totals validation:
 *   Sum all row percents. If the sum exceeds 1.0 (with 0.0001 float
 *   tolerance — template example totals to 1.0031 without crashing), every
 *   row's percent cell gets a cellErrors entry describing the overage.
 *   The error clears naturally when the sum drops back ≤ 1.0.
 */

export interface IngredientsGridProps {
  ingredients: Ingredient[]
  batchSizeG: number
  /**
   * Sprint 08b — non-persistent scale preview. When set, Target (g)
   * column computes against this value instead of `batchSizeG`. Doesn't
   * mark the formulation dirty. Pass `null` to fall back to the formula's
   * own batch size (default behavior).
   */
  scaleTo?: number | null
  onIngredientChange: (rowIndex: number, patch: Partial<Ingredient>) => void
  onAddRow: () => void
  onRowReorder?: (fromIndex: number, toIndex: number) => void
  readOnly?: boolean
  /**
   * Sprint 11 — when set, every ingredient row gets a trailing
   * "Sourcing" button via EditableGrid's rowActions slot. Disabled
   * (with tooltip) when the row has no `product_id` yet.
   */
  onShowSourcing?: (ingredient: Ingredient) => void
}

const PCT_TOLERANCE = 1.0001

export function IngredientsGrid({
  ingredients,
  batchSizeG,
  scaleTo,
  onIngredientChange,
  onAddRow,
  onRowReorder,
  readOnly,
  onShowSourcing,
}: IngredientsGridProps) {
  // The basis used for Target (g) calculations. When scaleTo is set
  // and positive, use it; otherwise fall back to the formula's batch
  // size. Sprint 08b D-1.
  const targetBasis =
    scaleTo != null && Number.isFinite(scaleTo) && scaleTo > 0
      ? scaleTo
      : batchSizeG
  const totalPercent = useMemo(
    () => ingredients.reduce((sum, i) => sum + (i.percent ?? 0), 0),
    [ingredients],
  )
  const totalIsOver = totalPercent > PCT_TOLERANCE

  const columns: EditableColumnDef<Ingredient>[] = useMemo(
    () => [
      {
        key: 'phase',
        header: 'Phase',
        width: '10%',
        readonly: readOnly,
        render: (v) => renderText(v as string | null),
      },
      {
        key: 'trade_name',
        header: 'Trade Name',
        width: '22%',
        readonly: readOnly,
        render: (v, row) => {
          const s = v as string
          if (s) {
            return (
              <span className="text-role-data-primary">
                {s}
                {row.product_id ? null : (
                  <span className="ml-2 text-xs text-tertiary">(unlinked)</span>
                )}
              </span>
            )
          }
          return <span className="text-tertiary">Pick ingredient…</span>
        },
        renderEditor: ({ row, onCommit, onCancel }) =>
          readOnly ? null : (
            <IngredientPicker
              value={
                row.product_id && row.trade_name
                  ? {
                      id: row.product_id,
                      name: row.trade_name,
                      default_code: null,
                      inci_names: row.inci_names ?? [],
                    }
                  : null
              }
              onChange={(next) => {
                if (next) {
                  // Snapshot inci_names from the picker so the cell
                  // updates immediately. The next GET-detail call will
                  // refresh from Odoo and set inci_source='odoo'.
                  onCommit({
                    trade_name: next.name,
                    product_id: next.id,
                    inci_names: next.inci_names,
                  })
                } else {
                  onCommit({ trade_name: row.trade_name, product_id: null })
                }
              }}
              editorMode
              onCancel={onCancel}
            />
          ),
      },
      {
        key: 'inci_names',
        header: 'INCI',
        width: '22%',
        // Cell editability switches per row based on whether the row
        // has a linked product. The grid's column-level `readonly` is
        // a coarse switch; per-row gating happens by leaving
        // renderEditor undefined when linked, plus a `readOnlyForRow`
        // signaled visually via the lock icon.
        readonly: readOnly,
        render: (v, row) => {
          const arr = (v as string[] | null) ?? []
          const linked = row.product_id != null
          if (arr.length === 0) {
            return linked ? (
              // Linked but no INCI from Odoo — surface why the cell
              // looks empty so operators don't try to type into it.
              <span className="flex items-center gap-1 text-tertiary">
                <Lock size={12} />
                <span className="text-xs italic">no INCI on Odoo product</span>
              </span>
            ) : (
              <span className="text-tertiary">—</span>
            )
          }
          return (
            <span className="flex items-center gap-1.5 text-role-data-secondary">
              {linked ? (
                <Lock
                  size={12}
                  className="flex-shrink-0 text-tertiary"
                  aria-label="INCI synced from Odoo product — read-only"
                />
              ) : null}
              <span className="truncate">{arr.join(', ')}</span>
            </span>
          )
        },
        renderEditor: ({ row, value, onCommit, onCancel }) => {
          if (readOnly) return null
          // When the row is linked to an Odoo product, INCI is owned by
          // Odoo (D-5: Odoo wins). Locking the cell here matches the
          // backend's `inci_source: "odoo"` contract.
          if (row.product_id != null) return null
          return (
            <InciEditor
              value={(value as string[] | null) ?? []}
              onCommit={(arr) => onCommit(arr)}
              onCancel={onCancel}
            />
          )
        },
      },
      {
        key: 'function',
        header: 'Function',
        width: '14%',
        readonly: readOnly,
        render: (v) => renderText(v as string | null),
      },
      {
        key: 'percent',
        header: 'Percent',
        width: '10%',
        numeric: true,
        readonly: readOnly,
        render: (v) => fmtPct(v as number),
        renderEditor: readOnly
          ? undefined
          : ({ value, onCommit, onCancel }) => (
              <PercentEditor
                value={value as number}
                onCommit={(n) => onCommit(n)}
                onCancel={onCancel}
              />
            ),
      },
      {
        key: 'target_weight' as unknown as keyof Ingredient,
        header: 'Target (g)',
        width: '10%',
        numeric: true,
        readonly: true,
        render: (_v, row) => {
          const grams = (row.percent ?? 0) * targetBasis
          return fmtWeight(grams)
        },
      },
      {
        key: 'notes',
        header: 'Notes',
        width: '12%',
        readonly: readOnly,
        render: (v) => renderText(v as string | null),
      },
    ],
    [targetBasis, readOnly],
  )

  const handleCellChange = (rowIndex: number, key: keyof Ingredient, value: unknown) => {
    // Trade name editor commits an object carrying both trade_name + product_id
    if (key === 'trade_name' && value && typeof value === 'object') {
      const obj = value as { trade_name: string; product_id: number | null }
      onIngredientChange(rowIndex, {
        trade_name: obj.trade_name,
        product_id: obj.product_id,
      })
      return
    }
    onIngredientChange(rowIndex, { [key]: value } as Partial<Ingredient>)
  }

  const meta = (
    <span className={totalIsOver ? 'text-danger-text font-semibold' : ''}>
      {ingredients.length} rows · total {fmtPct(totalPercent)}
      {totalIsOver ? ' — exceeds 100%' : ''}
    </span>
  )

  // Footer totals row — single, unambiguous place to show the sum.
  // Replaces per-cell error bubbles which stacked on every row and
  // overlapped the cell values. See Sprint 08 UI fix 2026-04-24.
  const totalsRow = useMemo(
    () =>
      (rows: Ingredient[]): Partial<
        Record<keyof Ingredient, { value: ReactNode; tone?: 'ok' | 'bad' }>
      > => {
        const sum = rows.reduce((acc, r) => acc + (r.percent ?? 0), 0)
        const over = sum > PCT_TOLERANCE
        return {
          percent: {
            value: fmtPct(sum),
            tone: over ? 'bad' : 'ok',
          },
          ['target_weight' as keyof Ingredient]: {
            value: fmtWeight(sum * targetBasis),
            tone: over ? 'bad' : 'ok',
          },
        }
      },
    [targetBasis],
  )

  return (
    <EditableGrid<Ingredient>
      rows={ingredients}
      columns={columns}
      onCellChange={handleCellChange}
      onAddRow={readOnly ? undefined : onAddRow}
      addRowLabel="Add ingredient"
      onRowReorder={readOnly ? undefined : onRowReorder}
      totalsRow={totalsRow}
      meta={meta}
      minTableWidth="1100px"
      // Grid sizes to content; the FormulationDetail pane owns the
      // vertical scroll so Notes + Procedure flow directly below the
      // last ingredient row instead of being pinned in a second
      // scroll region.
      maxHeight="none"
      rowActions={
        onShowSourcing
          ? (row) => (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={
                  row.product_id
                    ? `Sourcing for ${row.trade_name}`
                    : 'Pick a product to see sourcing'
                }
                title={
                  row.product_id
                    ? 'Show approved vendors + alternates'
                    : 'Pick a product to see sourcing'
                }
                disabled={!row.product_id}
                onClick={() => onShowSourcing(row)}
              >
                <Truck size={14} />
              </Button>
            )
          : undefined
      }
    />
  )
}

function renderText(v: string | null | undefined): ReactNode {
  if (!v || v === '') return <span className="text-tertiary">—</span>
  return <span>{v}</span>
}

function fmtPct(n: number): string {
  if (n == null || Number.isNaN(n)) return '—'
  return `${(n * 100).toFixed(4).replace(/\.?0+$/, '')}%`
}

function fmtWeight(n: number): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n === 0) return '0'
  if (n < 0.01) return n.toFixed(4)
  if (n < 1) return n.toFixed(3)
  if (n < 100) return n.toFixed(2)
  return n.toFixed(1)
}

// ─── Inline editors ──────────────────────────────────────────────────

function PercentEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: number | null
  onCommit: (v: number) => void
  onCancel: () => void
}) {
  // Display % form but store decimal. Round-trip on commit.
  const initial = value != null ? String((value * 100).toFixed(4).replace(/\.?0+$/, '')) : ''
  return (
    <input
      type="text"
      autoFocus
      defaultValue={initial}
      className="h-full w-full border-0 bg-transparent px-2 text-right outline-none focus:ring-2 focus:ring-accent"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const raw = (e.target as HTMLInputElement).value.trim()
          if (raw === '') {
            onCommit(0)
            return
          }
          const asPct = Number(raw.replace(/%$/, ''))
          if (Number.isFinite(asPct)) {
            onCommit(asPct / 100)
          } else {
            onCancel()
          }
        } else if (e.key === 'Escape') {
          e.stopPropagation()
          onCancel()
        }
      }}
      onBlur={(e) => {
        const raw = e.target.value.trim()
        if (raw === '') {
          onCommit(0)
          return
        }
        const asPct = Number(raw.replace(/%$/, ''))
        if (Number.isFinite(asPct)) {
          onCommit(asPct / 100)
        } else {
          onCancel()
        }
      }}
    />
  )
}

function InciEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: string[]
  onCommit: (v: string[]) => void
  onCancel: () => void
}) {
  const initial = value.join(', ')
  const commit = (raw: string) => {
    const arr = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    onCommit(arr)
  }
  return (
    <input
      type="text"
      autoFocus
      defaultValue={initial}
      className="h-full w-full border-0 bg-transparent px-2 outline-none focus:ring-2 focus:ring-accent"
      placeholder="comma-separated INCI names"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit((e.target as HTMLInputElement).value)
        } else if (e.key === 'Escape') {
          e.stopPropagation()
          onCancel()
        }
      }}
      onBlur={(e) => commit(e.target.value)}
    />
  )
}
